import React from 'react';
import { ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import DisplayName from './DisplayName';
import LanguageSwitcher from './LanguageSwitcher';
import { Language } from '../config/translations';

interface User {
  id: number;
  username: string;
  displayName?: string;
}

interface UserSubscription {
  tier: string;
  status: string;
  expiresAt: string | null;
  isActive: boolean;
  isTrial: boolean;
}

interface MapCacheStatus {
  isActive: boolean;
  cachedTiles: number;
  estimatedSavings: number;
}

interface NavigationProps {
  t: (key: any) => string;
  language: Language;
  onLanguageChange: (lang: Language) => void;
  isAuthenticated: boolean;
  user: User | null;
  userSubscription?: UserSubscription | null;
  mapCacheStatus: MapCacheStatus;
  mobileMenuOpen: boolean;
  onMobileMenuToggle: () => void;
  onShowContactModal: () => void;
  onShowFAQModal: () => void;
  onShowAboutModal: () => void;
  onShowPricingModal: () => void;
  onShowSettingsModal: () => void;
  onShowLoginModal: () => void;
  onShowRegisterModal: () => void;
  onShowSubscriptionModal: () => void;
  onLogout: () => void;
}

const Navigation: React.FC<NavigationProps> = ({
  t,
  language,
  onLanguageChange,
  isAuthenticated,
  user,
  userSubscription,
  mapCacheStatus,
  mobileMenuOpen,
  onMobileMenuToggle,
  onShowContactModal,
  onShowFAQModal,
  onShowAboutModal,
  onShowPricingModal,
  onShowSettingsModal,
  onShowLoginModal,
  onShowRegisterModal,
  onShowSubscriptionModal,
  onLogout,
}) => {
  // Helper function to get tier display info
  const getTierBadge = () => {
    if (!userSubscription) return null;
    
    // Map tier names to the current scheme (Free / Plus). Legacy paid tiers
    // (basic/bronze/silver/gold/premium) all collapse to the single paid "Plus".
    const getTierName = (tier: string): string => {
      if (!tier || tier === 'free') return 'Free';
      return 'Plus';
    };

    const tierName = getTierName(userSubscription.tier);
    const trialText = userSubscription.isTrial ? ' Trial' : '';
    const isPremium = tierName === 'Plus';
    
    return (
      <button
        onClick={(e) => {
          e.stopPropagation(); // Prevent settings modal from opening
          onShowSubscriptionModal();
        }}
        className="inline-flex items-center space-x-1 bg-gray-700 bg-opacity-60 px-2 py-1 rounded text-xs font-medium border border-gray-600 hover:bg-gray-600 hover:bg-opacity-70 transition-colors duration-200"
        title={t('nav_manageSubscription')}
      >
        <span className="text-gray-400">•</span>
        <span className={isPremium ? 'text-yellow-200' : 'text-gray-300'}>{tierName}{trialText}</span>
      </button>
    );
  };
  return (
    <>
      <nav className="relative z-10 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <h1 className="text-white text-2xl font-bold">Where2</h1>
          </div>
          
          {/* Map Cache Status Indicator - Always visible for testing */}
          {mapCacheStatus.isActive && (
            <div className="hidden md:flex items-center space-x-2 bg-blue-600 bg-opacity-80 text-white px-3 py-2 rounded-lg text-xs">
              <span className="emoji">🗺️</span>
              <span>{t('nav_mapCache')}: {mapCacheStatus.cachedTiles} {t('nav_tiles')}</span>
              <span className="text-blue-200">(~{mapCacheStatus.estimatedSavings.toFixed(1)}s {t('nav_saved')})</span>
            </div>
          )}
          
          {/* Desktop nav */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {/* Language Switcher - positioned to the left of Contact */}
              <LanguageSwitcher 
                language={language} 
                onLanguageChange={onLanguageChange} 
              />
              <button onClick={onShowContactModal} className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium">{t('contact')}</button>
              <button onClick={onShowFAQModal} className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium">{t('faq')}</button>
              <button onClick={onShowAboutModal} className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium">{t('about')}</button>
              <button onClick={onShowPricingModal} className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium">{t('pricing')}</button>
              {isAuthenticated ? (
                <div className="flex items-center space-x-2 max-w-xs">
                  <button
                    onClick={onShowSettingsModal}
                    className="flex items-center text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium min-w-0 flex-1 max-w-[200px]"
                    title={`${user?.displayName || user?.username} - ${t('settings')}`}
                  >
                    <div className="flex items-center space-x-2 min-w-0">
                      {user?.displayName ? (
                        <DisplayName displayName={user.displayName} />
                      ) : (
                        <div className="flex items-center space-x-2 min-w-0">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                            {user?.username?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-white text-sm truncate">
                            {user?.username}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                  {/* Subscription Tier Badge - always visible with flex-shrink-0 */}
                  <div className="flex-shrink-0">
                    {getTierBadge()}
                  </div>
                  {/* Logout Button */}
                  <button
                    onClick={onLogout}
                    className="flex-shrink-0 p-2 text-white hover:text-red-300 hover:bg-red-500/20 rounded-md transition-all duration-200 group"
                    title={t('nav_signOut')}
                    aria-label={t('nav_signOut')}
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={onShowLoginModal}
                    className="flex items-center text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    {t('login')}
                  </button>
                  <button
                    onClick={onShowRegisterModal}
                    className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700"
                  >
                    {t('register')}
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Mobile nav button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={onMobileMenuToggle}
              className="text-white focus:outline-none focus:ring-2 focus:ring-primary-500 p-2"
              aria-label="Open menu"
            >
              {/* Hamburger icon */}
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          </div>
        </div>
      </nav>
    
    {/* Mobile menu dropdown - moved outside nav to avoid stacking context issues */}
    {mobileMenuOpen && (
      <div className="md:hidden fixed inset-0 top-16 bg-black bg-opacity-90 z-[99999] shadow-lg">
        <div className="flex flex-col space-y-2 px-6 py-4 bg-primary-900 rounded-b-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
          <button onClick={() => { onShowContactModal(); onMobileMenuToggle(); }} className="text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 text-left">{t('contact')}</button>
          <button onClick={() => { onShowFAQModal(); onMobileMenuToggle(); }} className="text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 text-left">{t('faq')}</button>
          <button onClick={() => { onShowAboutModal(); onMobileMenuToggle(); }} className="text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 text-left">{t('about')}</button>
          <button onClick={() => { onShowPricingModal(); onMobileMenuToggle(); }} className="text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 text-left">{t('pricing')}</button>
          {isAuthenticated ? (
            <>
              <button
                onClick={() => { onShowSettingsModal(); onMobileMenuToggle(); }}
                className="flex items-center text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700"
              >
                <div className="flex items-center space-x-2">
                  {user?.displayName ? (
                    <DisplayName displayName={user.displayName || ''} />
                  ) : user ? (
                    <>
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {user?.username?.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-white text-base">
                        {user?.username}
                      </span>
                    </>
                  ) : null}
                </div>
              </button>
              {/* Mobile Subscription Tier Badge - separate button */}
              <div className="px-3 py-2">
                {getTierBadge()}
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => { onShowLoginModal(); onMobileMenuToggle(); }}
                className="text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 text-left"
              >
                {t('login')}
              </button>
              <button
                onClick={() => { onShowRegisterModal(); onMobileMenuToggle(); }}
                className="bg-primary-600 text-white px-3 py-2 rounded-md text-base font-medium hover:bg-primary-700 text-left"
              >
                {t('register')}
              </button>
            </>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default Navigation;

